/**
 * The one module in the application that knows the platform's own name.
 *
 * D19 puts the platform name in exactly three places — the sign-in footer, the
 * email footer and "About this instance" — and nowhere else. The name itself is
 * configuration (`VITE_PLATFORM_NAME`), never a literal in source, so that the
 * still-open naming decision cannot force a code change.
 *
 * There is deliberately no default. A default string is a string that can be
 * silently wrong in production. Instead:
 *
 * - in development the module throws on first import, loudly and with the fix
 *   in the message, so a missing variable is found in seconds;
 * - in production it renders nothing, so a misconfigured deploy shows a footer
 *   without a platform name rather than a placeholder in front of a client.
 *
 * `.env.example` carries the name decided on 2026-09-06. Keeping it in
 * configuration means renaming the product never touches source.
 */

const RAW: unknown = import.meta.env.VITE_PLATFORM_NAME;

const MISSING_MESSAGE = [
  'VITE_PLATFORM_NAME is not set.',
  '',
  'The platform name is configuration, never a literal in source.',
  'Copy .env.example to .env.local and set it. It carries the platform',
  'name; the name is configuration, so renaming never touches source.',
].join('\n');

function read(): string | null {
  const value = typeof RAW === 'string' ? RAW.trim() : '';
  if (value.length > 0) return value;
  if (import.meta.env.DEV) throw new Error(MISSING_MESSAGE);
  return null;
}

/**
 * The platform's own name, or `null` when it is not configured and we are not
 * in development. Callers must render nothing for `null` — never a fallback.
 */
export const platformName: string | null = read();

/** "Powered by <the configured name>", or `null` when there is none. */
export function poweredBy(): string | null {
  return platformName === null ? null : `Powered by ${platformName}`;
}
